<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header fin-header--sticky is-no-print">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.incomeStatement.title") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="expandAll">
              <v-icon small>unfold_more</v-icon>
            </v-btn>
            <span>{{ $t("finance.coa.expandAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="collapseAll">
              <v-icon small>unfold_less</v-icon>
            </v-btn>
            <span>{{ $t("finance.coa.collapseAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="exportExcel">
              <v-icon small>grid_on</v-icon>
            </v-btn>
            <span>Excel</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="downloadCsv">
              <v-icon small>file_download</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.csv") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="exportPrint">
              <v-icon small>print</v-icon>
            </v-btn>
            <span>{{ $t("finance.trialBalance.printPdf") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadReport">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.refresh") }}</span>
          </v-tooltip>
        </div>
        <div class="fin-filters">
          <v-text-field
            v-model="filters.from"
            type="date"
            :label="$t('finance.incomeStatement.fromDate')"
            hide-details
            class="fin-field"
          ></v-text-field>
          <v-text-field
            v-model="filters.to"
            type="date"
            :label="$t('finance.incomeStatement.toDate')"
            hide-details
            class="fin-field"
          ></v-text-field>
          <v-select
            v-model="filters.comparison"
            :items="comparisonOptions"
            item-text="text"
            item-value="value"
            :label="$t('finance.incomeStatement.comparison')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.currency"
            :items="currencyOptions"
            item-text="text"
            item-value="value"
            :label="$t('finance.balanceSheet.currency')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.max_level"
            :items="levelOptions"
            item-text="text"
            item-value="value"
            :label="$t('finance.trialBalance.maxLevel')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-btn small color="primary" @click="loadReport">{{ $t("finance.common.apply") }}</v-btn>
        </div>
      </div>

      <div v-if="hasReport" class="fin-summary is-no-print">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.incomeStatement.grossProfit") }}</span>
          <span class="fin-stat-value" :class="signClass(summaryFigure('gross_profit').current)">
            {{ formatValue(summaryFigure("gross_profit").current) }}
          </span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.incomeStatement.operatingProfit") }}</span>
          <span class="fin-stat-value" :class="signClass(summaryFigure('operating_profit').current)">
            {{ formatValue(summaryFigure("operating_profit").current) }}
          </span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.incomeStatement.profitBeforeTax") }}</span>
          <span class="fin-stat-value" :class="signClass(summaryFigure('profit_before_tax').current)">
            {{ formatValue(summaryFigure("profit_before_tax").current) }}
          </span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.incomeStatement.profitAfterTax") }}</span>
          <span class="fin-stat-value" :class="signClass(summaryFigure('profit_after_tax').current)">
            {{ formatValue(summaryFigure("profit_after_tax").current) }}
          </span>
        </div>
        <v-chip small :color="isProfit ? 'green' : 'red'" text-color="white">
          <v-icon small class="me-1">{{ isProfit ? "trending_up" : "trending_down" }}</v-icon>
          {{
            isProfit
              ? $t("finance.incomeStatement.profitShort")
              : $t("finance.incomeStatement.lossShort")
          }}
        </v-chip>
      </div>

      <div v-if="hasReport && excludedMovements" class="fin-alert-error is-no-print">
        <v-icon small color="#c62828">warning</v-icon>
        {{ $t("finance.incomeStatement.excludedWarning", { amount: formatValue(excludedMovements) }) }}
      </div>

      <div class="is-table-wrap fin-table-wrap">
        <table class="is-tree-table fin-table">
          <thead>
            <tr>
              <th>{{ $t("finance.common.code") }}</th>
              <th class="is-col-name">{{ $t("finance.incomeStatement.item") }}</th>
              <th class="fin-num-head">{{ $t("finance.incomeStatement.currentPeriod") }}</th>
              <th v-if="comparisonEnabled" class="fin-num-head">
                {{ $t("finance.incomeStatement.previousPeriod") }}
              </th>
              <th v-if="comparisonEnabled" class="fin-num-head">
                {{ $t("finance.incomeStatement.change") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, index) in displayRows">
              <tr v-if="row.type === 'header'" :key="'h-' + row.section" class="is-section-header-row">
                <td></td>
                <td :colspan="comparisonEnabled ? 4 : 2">{{ sectionLabel(row.section) }}</td>
              </tr>
              <tr
                v-else-if="row.type === 'account'"
                :key="'a-' + row.account.id"
                :class="rowClasses(row)"
              >
                <td class="fin-code">{{ row.account.code }}</td>
                <td class="is-col-name">
                  <span class="is-indent" :style="{ 'padding-inline-start': row.depth * 26 + 'px' }">
                    <v-btn
                      v-if="row.hasChildren"
                      icon
                      small
                      class="is-toggle is-no-print"
                      @click="toggleNode(row.account.id)"
                    >
                      <v-icon small>{{ isExpanded(row.account.id) ? "remove" : "add" }}</v-icon>
                    </v-btn>
                    <span v-else class="is-toggle-spacer"></span>
                    <span class="fin-ellipsis" :title="localizedAccountName(row.account)">{{ localizedAccountName(row.account) }}</span>
                  </span>
                </td>
                <td class="fin-num" :class="{ 'is-negative': row.account.value < 0 }">
                  {{ formatValue(row.account.value) }}
                </td>
                <td v-if="comparisonEnabled" class="fin-num" :class="{ 'is-negative': row.account.previous_value < 0 }">
                  {{ formatValue(row.account.previous_value) }}
                </td>
                <td v-if="comparisonEnabled" class="fin-num" :class="changeClass(row.account.change, row.account.section)">
                  {{ formatChange(row.account.change) }}
                </td>
              </tr>
              <tr v-else-if="row.type === 'subtotal'" :key="'s-' + row.section" class="is-subtotal-row">
                <td></td>
                <td>{{ subtotalLabel(row.section) }}</td>
                <td class="fin-num">{{ formatValue(summaryFigure(subtotalSummaryKey(row.section)).current) }}</td>
                <td v-if="comparisonEnabled" class="fin-num">
                  {{ formatValue(summaryFigure(subtotalSummaryKey(row.section)).previous) }}
                </td>
                <td
                  v-if="comparisonEnabled"
                  class="fin-num"
                  :class="changeClass(summaryFigure(subtotalSummaryKey(row.section)).change, row.section)"
                >
                  {{ formatChange(summaryFigure(subtotalSummaryKey(row.section)).change) }}
                </td>
              </tr>
              <tr
                v-else
                :key="'c-' + row.key + '-' + index"
                class="is-computed-row"
                :class="computedRowClass(row.key)"
              >
                <td class="fin-code">{{ row.key === "income_tax" ? incomeTaxCode : "" }}</td>
                <td>{{ computedLabel(row.key) }}</td>
                <td class="fin-num">{{ formatValue(summaryFigure(row.key).current) }}</td>
                <td v-if="comparisonEnabled" class="fin-num">
                  {{ formatValue(summaryFigure(row.key).previous) }}
                </td>
                <td
                  v-if="comparisonEnabled"
                  class="fin-num"
                  :class="changeClass(summaryFigure(row.key).change, row.key === 'income_tax' ? 'expense' : 'profit')"
                >
                  {{ formatChange(summaryFigure(row.key).change) }}
                </td>
              </tr>
            </template>
            <tr v-if="!hasReport && !loading">
              <td :colspan="comparisonEnabled ? 5 : 3" class="fin-empty">
                {{ $t("finance.coa.noResults") }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>
  </div>
</template>

<script>
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";
import { companyNow, companyToday } from "@/util/companyTime";

// Doc 3: statement layout — section order, its subtotal, and the computed
// profit lines emitted after each block.
const SECTION_ORDER = [
  "operating_revenue",
  "cost_of_revenue",
  "general_admin",
  "non_operating_revenue",
  "non_operating_expense",
];
const COMPUTED_AFTER = {
  cost_of_revenue: ["gross_profit"],
  general_admin: ["operating_profit"],
  non_operating_expense: ["profit_before_tax", "income_tax", "profit_after_tax"],
};
const SUBTOTAL_SUMMARY_KEYS = {
  operating_revenue: "operating_revenue",
  cost_of_revenue: "cost_of_revenue",
  general_admin: "general_admin_expenses",
  non_operating_revenue: "non_operating_revenue",
  non_operating_expense: "non_operating_expense",
};

export default {
  name: "IncomeStatement",
  title() {
    return this.$t("finance.incomeStatement.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent },
  data() {
    // Final-version doc 2026-10-05: the company (Damascus) day on the
    // server-synced clock, not the PC calendar.
    const today = companyToday();
    const monthStart = today.slice(0, 8) + "01";

    return {
      loading: false,
      report: null,
      expandedIds: {},
      filters: {
        // Doc 2: month-to-date by default.
        from: monthStart,
        to: today,
        comparison: "none",
        // Doc 4.7: value display in the new (redenominated) or old currency.
        currency: "new",
        max_level: null,
      },
    };
  },
  computed: {
    hasReport() {
      return !!(this.report && this.report.summary);
    },
    comparisonEnabled() {
      return !!(this.report && this.report.comparison_enabled);
    },
    incomeTaxCode() {
      return (this.report && this.report.income_tax_code) || "";
    },
    isProfit() {
      return this.summaryFigure("profit_after_tax").current >= 0;
    },
    // Nonzero when the period has posted movements no statement bucket can
    // carry (closing accounts, mis-parented accounts) — the report's own
    // integrity warning, like the siblings' balance banners.
    excludedMovements() {
      return this.summaryFigure("excluded_movements").current || 0;
    },
    reportRows() {
      return (this.report && this.report.rows) || [];
    },
    rowsById() {
      const map = {};
      this.reportRows.forEach((row) => {
        map[row.id] = row;
      });
      return map;
    },
    childrenByParent() {
      // Server rows arrive in tree order. A parent missing from the payload
      // (the class roots are never sent; max_level can cut others) turns its
      // children into roots of their section.
      const map = {};
      this.reportRows.forEach((row) => {
        const parentId = row.parent_id && this.rowsById[row.parent_id] ? row.parent_id : "root-" + row.section;
        if (!map[parentId]) {
          map[parentId] = [];
        }
        map[parentId].push(row);
      });
      return map;
    },
    displayRows() {
      return this.buildLayoutRows(true);
    },
    levelOptions() {
      const options = [{ text: this.$t("finance.trialBalance.allLevels"), value: null }];
      for (let level = 1; level <= 8; level++) {
        options.push({ text: String(level), value: level });
      }
      return options;
    },
    currencyOptions() {
      return [
        { text: this.$t("finance.balanceSheet.currencyNew"), value: "new" },
        { text: this.$t("finance.balanceSheet.currencyOld"), value: "old" },
      ];
    },
    comparisonOptions() {
      return [
        { text: this.$t("finance.incomeStatement.comparisonNone"), value: "none" },
        { text: this.$t("finance.incomeStatement.comparisonPreviousPeriod"), value: "previous_period" },
        { text: this.$t("finance.incomeStatement.comparisonPreviousYear"), value: "previous_year" },
      ];
    },
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    buildParams(extra) {
      const params = {};
      if (this.filters.from) params.from = this.filters.from;
      if (this.filters.to) params.to = this.filters.to;
      if (this.filters.comparison && this.filters.comparison !== "none") {
        params.comparison = this.filters.comparison;
      }
      if (this.filters.max_level) params.max_level = this.filters.max_level;
      return Object.assign(params, extra || {});
    },
    loadReport() {
      if (this.filters.comparison !== "none" && (!this.filters.from || !this.filters.to)) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.incomeStatement.comparisonNeedsDates"), "red");
        return;
      }
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_INCOME_STATEMENT, { params: this.buildParams() })
        .then(
          function (response) {
            this.report = response.data.data || null;
            // Accountants read the whole statement by default.
            this.expandAll();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.incomeStatement.failedLoad")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    // The statement in layout order: per section a header row, the account
    // tree, its subtotal, then the computed profit lines the doc places
    // after that block. honorExpand=false gives exports the full tree.
    buildLayoutRows(honorExpand) {
      if (!this.hasReport) {
        return [];
      }
      // No cycle guard needed: every row files under exactly one parent
      // key, so a corrupt parent loop is simply unreachable from the
      // section roots the walk starts at.
      const rows = [];
      const walk = (parentKey, depth) => {
        (this.childrenByParent[parentKey] || []).forEach((account) => {
          const hasChildren = !!(this.childrenByParent[account.id] || []).length;
          rows.push({ type: "account", account, depth, hasChildren });
          if (hasChildren && (!honorExpand || this.expandedIds[account.id])) {
            walk(account.id, depth + 1);
          }
        });
      };

      SECTION_ORDER.forEach((section) => {
        rows.push({ type: "header", section });
        walk("root-" + section, 0);
        rows.push({ type: "subtotal", section });
        (COMPUTED_AFTER[section] || []).forEach((key) => {
          if (key === "income_tax" && !this.incomeTaxCode && !this.summaryFigure(key).current) {
            // A chart without the 5225 account has no tax line to show.
            return;
          }
          rows.push({ type: "computed", key });
        });
      });
      return rows;
    },
    isExpanded(id) {
      return !!this.expandedIds[id];
    },
    toggleNode(id) {
      const next = Object.assign({}, this.expandedIds);
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      this.expandedIds = next;
    },
    expandAll() {
      const next = {};
      this.reportRows.forEach((row) => {
        next[row.id] = true;
      });
      this.expandedIds = next;
    },
    collapseAll() {
      this.expandedIds = {};
    },
    rowClasses(row) {
      return {
        "is-level-2": row.account.level <= 2,
        "is-parent-row": row.hasChildren,
        "is-inactive-row": row.account.is_active === false,
      };
    },
    computedRowClass(key) {
      if (key === "income_tax") {
        return "is-tax-row";
      }
      // Doc 5.3: profit lines green, losses red.
      return this.summaryFigure(key).current >= 0 ? "is-profit-row" : "is-loss-row";
    },
    signClass(value) {
      return Number(value || 0) < 0 ? "is-negative" : "is-positive";
    },
    localizedAccountName(account) {
      if (!account) {
        return "-";
      }

      if (this.$i18n && this.$i18n.locale === "ar" && account.name_ar) {
        return account.name_ar;
      }

      return account.name || account.name_ar || "-";
    },
    summaryFigure(key) {
      const figure = this.hasReport ? this.report.summary[key] : null;
      return figure || { current: 0, previous: null, change: null };
    },
    sectionLabel(section) {
      return this.$t("finance.incomeStatement.sections." + section);
    },
    subtotalLabel(section) {
      return this.$t("finance.incomeStatement.subtotals." + section);
    },
    subtotalSummaryKey(section) {
      return SUBTOTAL_SUMMARY_KEYS[section] || section;
    },
    computedLabel(key) {
      const map = {
        gross_profit: "grossProfit",
        operating_profit: "operatingProfit",
        profit_before_tax: "profitBeforeTax",
        income_tax: "incomeTax",
        profit_after_tax: "profitAfterTax",
      };
      return this.$t("finance.incomeStatement." + (map[key] || key));
    },
    // Doc 4.7 currency filter: "new" shows the redenominated value (÷100,
    // same as the money mixin), "old" shows the stored value as-is.
    formatValue(value) {
      if (value === null || value === undefined) {
        return "";
      }
      if (this.filters.currency === "old") {
        return Number(value || 0).toLocaleString("en", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
      return this.formatMoney(value);
    },
    // The displayed (currency-scaled, 2-dp rounded) number — the sign and
    // the good/bad color must follow what the reader actually sees, or a
    // sub-cent change renders as "+0.00".
    displayNumber(value) {
      if (value === null || value === undefined) {
        return null;
      }
      const scaled = this.filters.currency === "old" ? Number(value) : Number(value) / 100;
      return Math.round(scaled * 100) / 100;
    },
    formatChange(value) {
      const rounded = this.displayNumber(value);
      if (rounded === null) {
        return "";
      }
      const formatted = this.formatValue(value);
      return rounded > 0 ? "+" + formatted : formatted;
    },
    // Doc 5.3 reading: color the change by what it means, not by its sign —
    // more revenue/profit is good (green), more expense is bad (red).
    changeClass(value, sectionKind) {
      const rounded = this.displayNumber(value);
      if (!rounded) {
        return "";
      }
      const revenueLike =
        sectionKind === "operating_revenue" ||
        sectionKind === "non_operating_revenue" ||
        sectionKind === "profit";
      const good = revenueLike ? rounded > 0 : rounded < 0;
      return good ? "is-positive" : "is-negative";
    },
    exportFileStamp() {
      // Final-version doc 2026-10-05: company day, not the PC calendar.
      return companyToday();
    },
    periodLabel() {
      // Labeled from the loaded report's own period, not the live filter
      // inputs — they diverge when dates are edited without pressing Apply.
      const current = (this.report && this.report.periods && this.report.periods.current) || {};
      const parts = [
        this.$t("finance.incomeStatement.fromDate") + " " + (current.from || "—"),
        this.$t("finance.incomeStatement.toDate") + " " + (current.to || "—"),
      ];
      if (this.comparisonEnabled && this.report.periods && this.report.periods.previous) {
        parts.push(
          this.$t("finance.incomeStatement.previousPeriod") +
            ": " +
            this.report.periods.previous.from +
            " → " +
            this.report.periods.previous.to
        );
      }
      return parts.join(" | ");
    },
    downloadCsv() {
      if (this.filters.comparison !== "none" && (!this.filters.from || !this.filters.to)) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.incomeStatement.comparisonNeedsDates"), "red");
        return;
      }
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_INCOME_STATEMENT, {
          params: this.buildParams({ format: "csv" }),
          responseType: "blob",
        })
        .then(
          function (response) {
            const blob = new Blob([response.data], { type: response.headers["content-type"] || "text/csv" });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = "income-statement-" + this.exportFileStamp() + ".csv";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.reports.failedDownloadReport")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    exportExcel() {
      if (!this.hasReport) {
        return;
      }
      // Real .xlsx built client-side from the loaded report (doc 5.4), in
      // statement order with section styling; the CSV endpoint stays
      // available for raw integrations.
      const rows = this.buildLayoutRows(false);
      const isArabic = this.$i18n && this.$i18n.locale === "ar";
      const useNewCurrency = this.filters.currency !== "old";
      const comparison = this.comparisonEnabled;

      import(/* webpackChunkName: "xlsx" */ "xlsx-js-style")
        .then(
          function (module) {
            const XLSX = module.default || module;
            const moneyFormat = "#,##0.00";
            // Stored amounts carry the pre-redenomination scale; the export
            // follows the currency filter (÷100 for the new currency).
            const money = (value) =>
              value === null || value === undefined
                ? ""
                : useNewCurrency
                ? Number(value || 0) / 100
                : Number(value || 0);

            const header = [
              this.$t("finance.common.code"),
              this.$t("finance.incomeStatement.item"),
              this.$t("finance.coa.level"),
              this.$t("finance.incomeStatement.currentPeriod"),
            ];
            if (comparison) {
              header.push(this.$t("finance.incomeStatement.previousPeriod"));
              header.push(this.$t("finance.incomeStatement.change"));
            }
            const data = rows.map(
              function (row) {
                if (row.type === "header") {
                  return ["", this.sectionLabel(row.section), "", ""].concat(comparison ? ["", ""] : []);
                }
                if (row.type === "subtotal") {
                  const figure = this.summaryFigure(this.subtotalSummaryKey(row.section));
                  const cells = ["", this.subtotalLabel(row.section), "", money(figure.current)];
                  if (comparison) {
                    cells.push(money(figure.previous));
                    cells.push(money(figure.change));
                  }
                  return cells;
                }
                if (row.type === "computed") {
                  const figure = this.summaryFigure(row.key);
                  const cells = [
                    row.key === "income_tax" ? String(this.incomeTaxCode || "") : "",
                    this.computedLabel(row.key),
                    "",
                    money(figure.current),
                  ];
                  if (comparison) {
                    cells.push(money(figure.previous));
                    cells.push(money(figure.change));
                  }
                  return cells;
                }
                const cells = [
                  String(row.account.code || ""),
                  this.localizedAccountName(row.account),
                  Number(row.account.level || 1),
                  money(row.account.value),
                ];
                if (comparison) {
                  cells.push(money(row.account.previous_value));
                  cells.push(money(row.account.change));
                }
                return cells;
              }.bind(this)
            );

            const ws = XLSX.utils.aoa_to_sheet([header].concat(data));
            const cols = [{ wch: 14 }, { wch: 52 }, { wch: 8 }, { wch: 18 }];
            if (comparison) {
              cols.push({ wch: 18 });
              cols.push({ wch: 18 });
            }
            ws["!cols"] = cols;

            const thin = { style: "thin", color: { rgb: "9E9E9E" } };
            const borders = { top: thin, bottom: thin, left: thin, right: thin };
            for (let r = 0; r <= data.length; r++) {
              for (let c = 0; c < header.length; c++) {
                const cell = ws[XLSX.utils.encode_cell({ r: r, c: c })];
                if (!cell) {
                  continue;
                }
                if (r === 0) {
                  cell.s = {
                    font: { bold: true },
                    fill: { patternType: "solid", fgColor: { rgb: "ECEFF1" } },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                  continue;
                }
                const row = rows[r - 1];
                if (row.type === "header") {
                  cell.s = {
                    font: { bold: true },
                    fill: { patternType: "solid", fgColor: { rgb: "E0E0E0" } },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                } else if (row.type === "subtotal") {
                  cell.s = {
                    font: { bold: true },
                    fill: { patternType: "solid", fgColor: { rgb: "E3F2FD" } },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                } else if (row.type === "computed") {
                  const positive = this.summaryFigure(row.key).current >= 0;
                  cell.s = {
                    font: { bold: true },
                    fill: {
                      patternType: "solid",
                      fgColor: {
                        rgb: row.key === "income_tax" ? "FFF3E0" : positive ? "E8F5E9" : "FFCDD2",
                      },
                    },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                } else {
                  const bold = Number(row.account.level || 1) <= 2;
                  cell.s = {
                    font: { bold: bold },
                    border: borders,
                    alignment:
                      c === 1
                        ? { indent: Math.min(row.depth, 15), vertical: "center" }
                        : { vertical: "center" },
                  };
                }
                if (c >= 3) {
                  cell.z = moneyFormat;
                }
              }
            }

            const wb = XLSX.utils.book_new();
            // Sheet reads right-to-left for the Arabic report.
            wb.Workbook = { Views: [{ RTL: !!isArabic }] };
            XLSX.utils.book_append_sheet(wb, ws, this.$t("finance.incomeStatement.pageTitle"));
            XLSX.writeFile(wb, "income-statement-" + this.exportFileStamp() + ".xlsx");
          }.bind(this)
        )
        .catch(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.reports.failedDownloadReport"), "red");
          }.bind(this)
        );
    },
    escapeHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    exportPrint() {
      if (!this.hasReport) {
        return;
      }
      // Dedicated print document (doc 5.4: print + save-as-PDF): no app
      // chrome, repeated table header per page, RTL-aware. The browser's
      // "Save as PDF" printer renders the Arabic faithfully.
      const rows = this.buildLayoutRows(false);
      const comparison = this.comparisonEnabled;
      const isArabic = this.$i18n && this.$i18n.locale === "ar";
      const esc = this.escapeHtml;
      const title = this.$t("finance.incomeStatement.pageTitle");
      // Final-version doc 2026-10-05: company (Damascus) time, not the PC's.
      const stamp = companyNow().format("YYYY-MM-DD HH:mm:ss");

      const headerLabels = [
        this.$t("finance.common.code"),
        this.$t("finance.incomeStatement.item"),
        this.$t("finance.incomeStatement.currentPeriod"),
      ];
      if (comparison) {
        headerLabels.push(this.$t("finance.incomeStatement.previousPeriod"));
        headerLabels.push(this.$t("finance.incomeStatement.change"));
      }
      const headerCells = headerLabels
        .map(function (label) {
          return "<th>" + esc(label) + "</th>";
        })
        .join("");

      const numCells = function (figure, formatValue, formatChange) {
        let cells = '<td class="num">' + esc(formatValue(figure.current)) + "</td>";
        if (comparison) {
          cells += '<td class="num">' + esc(formatValue(figure.previous)) + "</td>";
          cells += '<td class="num">' + esc(formatChange(figure.change)) + "</td>";
        }
        return cells;
      };

      const bodyRows = rows
        .map(
          function (row) {
            if (row.type === "header") {
              return (
                '<tr class="section-header"><td></td><td colspan="' +
                (comparison ? 4 : 2) +
                '">' +
                esc(this.sectionLabel(row.section)) +
                "</td></tr>"
              );
            }
            if (row.type === "subtotal") {
              const figure = this.summaryFigure(this.subtotalSummaryKey(row.section));
              return (
                '<tr class="subtotal"><td></td><td>' +
                esc(this.subtotalLabel(row.section)) +
                "</td>" +
                numCells(figure, this.formatValue.bind(this), this.formatChange.bind(this)) +
                "</tr>"
              );
            }
            if (row.type === "computed") {
              const figure = this.summaryFigure(row.key);
              const cls =
                row.key === "income_tax" ? "tax" : figure.current >= 0 ? "profit" : "loss";
              return (
                '<tr class="computed ' + cls + '"><td class="code">' +
                esc(row.key === "income_tax" ? this.incomeTaxCode : "") +
                "</td><td>" +
                esc(this.computedLabel(row.key)) +
                "</td>" +
                numCells(figure, this.formatValue.bind(this), this.formatChange.bind(this)) +
                "</tr>"
              );
            }
            const level = Number(row.account.level || 1);
            const figure = {
              current: row.account.value,
              previous: row.account.previous_value,
              change: row.account.change,
            };
            return (
              '<tr class="' + (level <= 2 ? "lvl-2" : "") + '">' +
              '<td class="code">' + esc(row.account.code) + "</td>" +
              '<td><span style="padding-inline-start:' + row.depth * 16 + 'px">' +
              esc(this.localizedAccountName(row.account)) + "</span></td>" +
              numCells(figure, this.formatValue.bind(this), this.formatChange.bind(this)) +
              "</tr>"
            );
          }.bind(this)
        )
        .join("");

      const doc =
        "<!doctype html>" +
        '<html dir="' + (isArabic ? "rtl" : "ltr") + '" lang="' + (isArabic ? "ar" : "en") + '">' +
        '<head><meta charset="utf-8"><title>' + esc(title) + "</title><style>" +
        'body{font-family:"Segoe UI",Tahoma,Arial,sans-serif;color:#111;margin:0;padding:24px;}' +
        "header{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #111;padding-bottom:8px;margin-bottom:14px;}" +
        "h1{font-size:20px;margin:0;}" +
        ".meta{font-size:12px;color:#555;text-align:end;}" +
        "table{width:100%;border-collapse:collapse;font-size:12px;}" +
        "th,td{border:1px solid #888;padding:4px 8px;text-align:start;vertical-align:top;}" +
        "thead{display:table-header-group;}" +
        "thead th{background:#eee;font-weight:700;}" +
        "tr{page-break-inside:avoid;}" +
        ".section-header td{font-weight:700;font-size:13px;background:#e0e0e0;}" +
        ".lvl-2 td{font-weight:700;}" +
        ".subtotal td{font-weight:700;background:#e3f2fd;}" +
        ".computed td{font-weight:700;}" +
        ".computed.profit td{background:#e8f5e9;}" +
        ".computed.loss td{background:#ffcdd2;}" +
        ".computed.tax td{background:#fff3e0;}" +
        ".code{white-space:nowrap;}" +
        ".num{text-align:end;direction:ltr;unicode-bidi:embed;white-space:nowrap;}" +
        "@page{size:A4;margin:12mm;}" +
        "</style></head><body>" +
        "<header><h1>" + esc(title) + '</h1><div class="meta">' +
        esc(this.periodLabel()) + "<br>" +
        esc(this.$t("finance.coa.exportedAt")) + ": " + esc(stamp) + "</div></header>" +
        "<table><thead><tr>" + headerCells + "</tr></thead><tbody>" + bodyRows + "</tbody></table>" +
        "</body></html>";

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.reports.failedDownloadReport"), "red");
        return;
      }
      printWindow.document.write(doc);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(function () {
        printWindow.print();
      }, 400);
    },
  },
};
</script>

<style scoped>
/* Table chrome comes from the shared .fin-table layer (finance-compact.css,
   final-version doc 2026-10-05). */
.is-table-wrap {
  --fin-table-max-h: calc(100vh - 250px);
  min-height: 320px;
}
/* Doc 5.3 visual rules: main sections bold with a distinct band. */
.is-section-header-row td {
  font-weight: 700;
  font-size: 14px;
  background: #e0e0e0;
}
.is-parent-row td {
  background: #f5f7fa;
}
.is-level-2 td {
  font-weight: 700;
}
.is-inactive-row td {
  color: rgba(0, 0, 0, 0.4);
}
.is-subtotal-row td {
  font-weight: 700;
  background: #e3f2fd;
}
.is-computed-row td {
  font-weight: 700;
}
/* Doc 5.3: net profit green, loss red, the tax line stands apart. */
.is-profit-row td {
  background: #e8f5e9;
}
.is-loss-row td {
  background: #ffcdd2;
}
.is-tax-row td {
  background: #fff3e0;
}
.is-indent {
  display: inline-flex;
  align-items: center;
}
.is-toggle {
  margin: 0;
  margin-inline-end: 4px;
  height: 22px;
  width: 22px;
}
.is-toggle-spacer {
  display: inline-block;
  width: 26px;
}
.is-negative {
  color: #c62828;
}
.is-positive {
  color: #2e7d32;
}
@media print {
  .is-no-print {
    display: none !important;
  }
}
</style>
